import { useMemo, type CSSProperties } from 'react'
import type { Assignee, VisitListResponse } from '@fitting-room/contracts'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { VISIT_STATUS_LABEL, markByAssignee } from '../../lib/appointments'
import { buildMonthGrid, monthLabel, shiftMonth, weekdayLabels } from '../../lib/months'
import { BOUTIQUE_LOCALE, boutiqueDayOf, boutiqueTime, boutiqueToday } from '../../lib/boutique'
import { AppointmentActionMenu } from './AppointmentActionMenu'
import { StaffMark } from './StaffMark'

type VisitRow = VisitListResponse['visits'][number]

/** How many appointments fit on a square before it starts saying "+ 3 more". */
const CHIPS_PER_DAY = 3

/**
 * How busy a day looks, in three steps.
 *
 * These are a LEGIBILITY band, not a capacity claim: nothing in the product
 * knows how many fittings a boutique can take at once, so the grid must not
 * imply it does. What the three steps buy is a month you can read at arm's
 * length — the busy weeks stand out from the quiet ones without counting.
 * Lightness moves with each step as well as hue, so the steps survive being
 * seen by someone who cannot tell the warm colours apart.
 */
function loadStep(count: number): 'none' | 'mid' | 'high' | 'full' {
  if (count >= 7) return 'full'
  if (count >= 5) return 'high'
  if (count >= 3) return 'mid'
  return 'none'
}

/**
 * A chip's fill, from the owner's color for its appointment type.
 *
 * A wash of the color rather than the color itself, with the color on the
 * leading edge: the owner picks any color at all, and a chip filled with a
 * deep one would be unreadable under black text. A type with no color keeps
 * the console's own chip, toned by the fitting room's status as before.
 */
function chipStyle(color: string | undefined): CSSProperties | undefined {
  if (!color) return undefined
  return {
    background: `color-mix(in srgb, ${color} 22%, #fff)`,
    boxShadow: `inset 3px 0 0 ${color}`,
    color: 'var(--st-ink)',
  }
}

/**
 * The month, as a grid of days with each day's appointments on it.
 *
 * Days are grouped on the showroom's own calendar day, which is the same
 * day the API filters on — a grid that regrouped by the viewer's local day
 * would disagree with the list view it sits next to.
 *
 * After tperent's month: the color is the appointment type's
 * (`backOffice.tierColors`), the emoji is who has it, and the status is drawn
 * as a treatment rather than a color — struck through when it is not going
 * to happen, faded once it has — so the three never compete for one channel.
 * Right-click (or a long press) on a chip changes its status in place.
 */
export function CalendarMonthView({
  month,
  visits,
  loading,
  onMonthChange,
  onOpenVisit,
  onOpenDay,
  tierColors = {},
  tierLabels,
  members,
}: {
  readonly month: string
  readonly visits: readonly VisitRow[]
  readonly loading: boolean
  readonly onMonthChange: (month: string) => void
  readonly onOpenVisit: (visitId: string, siblingIds: readonly string[]) => void
  readonly onOpenDay: (day: string) => void
  /** Appointment type key → its color, from the back office's settings. */
  readonly tierColors?: Readonly<Record<string, string>>
  /** Appointment type key → its name, for the legend. */
  readonly tierLabels?: ReadonlyMap<string, string>
  readonly members?: readonly Assignee[]
}) {
  const locale = BOUTIQUE_LOCALE
  const today = boutiqueToday()

  const byDay = useMemo(() => {
    const map = new Map<string, VisitRow[]>()
    for (const visit of visits) {
      const day = boutiqueDayOf(visit.scheduledAt)
      const list = map.get(day)
      if (list) list.push(visit)
      else map.set(day, [visit])
    }
    for (const list of map.values()) list.sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt))
    return map
  }, [visits])

  const marks = useMemo(() => markByAssignee(members), [members])
  const names = useMemo(() => new Map((members ?? []).map((m) => [m.id, m.displayName])), [members])
  const grid = useMemo(() => buildMonthGrid(month), [month])
  const legend = Object.entries(tierColors).filter(([key]) => tierLabels?.has(key))
  const weekdays = useMemo(() => weekdayLabels(locale), [locale])
  const orderedIds = useMemo(
    () => [...visits].sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt)).map((v) => v.id),
    [visits],
  )

  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <button type="button" className="st-iconbtn" onClick={() => onMonthChange(shiftMonth(month, -1))} aria-label="Previous month">
          <ChevronLeft size={16} aria-hidden />
        </button>
        <button type="button" className="st-iconbtn" onClick={() => onMonthChange(shiftMonth(month, 1))} aria-label="Next month">
          <ChevronRight size={16} aria-hidden />
        </button>
        <h2 className="st-display ml-1 text-xl">{monthLabel(month, locale)}</h2>
        {loading ? <span className="st-meta">Loading…</span> : null}
      </div>

      {/* What the colors and marks mean, stated once above the grid. The
          colors are chosen in Settings and the marks in Settings → Team; this
          only reads them. */}
      {legend.length > 0 || marks.size > 0 ? (
        <ul className="st-meta mb-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs" aria-label="Calendar key">
          {legend.map(([key, color]) => (
            <li key={key} className="flex items-center gap-1.5">
              <span className="st-tier-dot" style={{ background: color }} aria-hidden />
              {tierLabels?.get(key)}
            </li>
          ))}
          {[...marks].map(([id, mark]) => (
            <li key={id} className="flex items-center gap-1">
              <StaffMark mark={mark} name={names.get(id)} />
              {names.get(id)}
            </li>
          ))}
        </ul>
      ) : null}

      <div className="st-grid">
        {weekdays.map((label) => (
          <div key={label} className="st-weekday">
            {label}
          </div>
        ))}

        {grid.map((square) => {
          const dayVisits = byDay.get(square.iso) ?? []
          const shown = dayVisits.slice(0, CHIPS_PER_DAY)
          const hidden = dayVisits.length - shown.length
          const step = loadStep(dayVisits.length)

          return (
            <div key={square.iso} className="st-day" data-outside={!square.inMonth} data-today={square.iso === today}>
              <div className="flex items-baseline justify-between gap-1">
                <button
                  type="button"
                  className="st-day-number"
                  onClick={() => onOpenDay(square.iso)}
                  aria-label={`Open ${square.iso}`}
                >
                  {square.dayOfMonth}
                </button>
                {dayVisits.length > 0 ? (
                  <span className="st-day-load" data-load={step}>
                    {dayVisits.length}
                  </span>
                ) : null}
              </div>

              {shown.map((visit) => {
                const assignee = visit.assigneeId ? names.get(visit.assigneeId) : undefined
                return (
                  <AppointmentActionMenu key={visit.id} visitId={visit.id} status={visit.status}>
                    <button
                      type="button"
                      className="st-day-chip"
                      data-tone={visit.sessionStatus}
                      data-status={visit.status}
                      style={chipStyle(tierColors[visit.tierKey])}
                      onClick={() => onOpenVisit(visit.id, orderedIds)}
                      // The treatment says the status without words, so the
                      // title says it with them, and who has it.
                      title={[
                        `${boutiqueTime(visit.scheduledAt)} · ${visit.customerName ?? 'No name'}`,
                        tierLabels?.get(visit.tierKey),
                        visit.status === 'booked' ? null : VISIT_STATUS_LABEL[visit.status],
                        assignee,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    >
                      <StaffMark mark={visit.assigneeId ? marks.get(visit.assigneeId) : undefined} name={assignee} />
                      <span className="tabular-nums">{boutiqueTime(visit.scheduledAt)}</span>
                      <span className="truncate">{visit.customerName ?? 'No name'}</span>
                    </button>
                  </AppointmentActionMenu>
                )
              })}

              {hidden > 0 ? (
                <button type="button" className="st-day-load text-left underline" onClick={() => onOpenDay(square.iso)}>
                  + {hidden} more
                </button>
              ) : null}
            </div>
          )
        })}
      </div>
    </div>
  )
}
